import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBootstrap } from '../context/BootstrapContext.jsx';

const initialState = {
  loading: false,
  error: null,
  items: [],
  selected: '',
  isSingle: false,
  disabled: true,
  count: 0,
  details: null,
  detailsLoading: false,
  detailsError: null
};

const normalizeResponse = (payload) => {
  if (!payload) {
    return { items: [], isSingle: false, count: 0 };
  }
  if (Array.isArray(payload)) {
    return {
      items: payload,
      isSingle: payload.length === 1,
      count: payload.length
    };
  }
  const items = payload.sucursales || [];
  const isSingle = Boolean(payload.is_single_restaurant) || items.length === 1;
  const count = payload.count || items.length;
  return { items, isSingle, count };
};

export const useRestaurants = () => {
  const bootstrap = useBootstrap();
  const [state, setState] = useState(initialState);
  const pendingDispatch = useRef(false);

  const fetchRestaurants = useCallback(async () => {
    if (!bootstrap?.authenticated) {
      setState(initialState);
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch('/api/get_sucursales');
      if (!response.ok) {
        throw new Error(`Error ${response.status}`);
      }
      const payload = await response.json();
      const { items, isSingle, count } = normalizeResponse(payload);
      const normalizedItems = items.filter(Boolean).map((value) => ({
        value,
        label: value
      }));
      const nextSelected = isSingle && normalizedItems.length > 0 ? normalizedItems[0].value : '';
      pendingDispatch.current = Boolean(nextSelected && isSingle);
      setState({
        loading: false,
        error: null,
        items: normalizedItems,
        selected: nextSelected,
        isSingle,
        disabled: normalizedItems.length === 0,
        count
      });
    } catch (error) {
      setState({
        ...initialState,
        loading: false,
        error: error.message || 'No se pudieron cargar las sucursales'
      });
    }
  }, [bootstrap?.authenticated]);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  useEffect(() => {
    if (!state.selected) {
      setState((prev) => ({
        ...prev,
        details: null,
        detailsLoading: false,
        detailsError: null
      }));
      return;
    }

    let cancelled = false;
    setState((prev) => ({
      ...prev,
      detailsLoading: true,
      detailsError: null
    }));

    const fetchDetails = async () => {
      try {
        console.log('[useRestaurants] Fetching details for:', state.selected);
        const response = await fetch(`/api/get_restaurante_details/${state.selected}`);
        if (!response.ok) {
          throw new Error(`Error ${response.status}`);
        }
        const payload = await response.json();
        console.log('[useRestaurants] Payload received:', payload);
        
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            details: payload,
            detailsLoading: false,
            detailsError: null
          }));
        }
      } catch (error) {
        console.error('[useRestaurants] Error fetching details:', error);
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            details: null,
            detailsLoading: false,
            detailsError: error.message || 'No se pudo obtener la información de la sucursal'
          }));
        }
      }
    };

    fetchDetails();

    return () => {
      cancelled = true;
    };
  }, [state.selected]);

  const setSelected = useCallback((value) => {
    setState((prev) => ({ ...prev, selected: value }));
  }, []);

  return useMemo(() => ({
    ...state,
    setSelected,
    pendingDispatch
  }), [state, setSelected]);
};
